import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ArrowLeft,
  Search,
  SlidersHorizontal,
} from "lucide-react";
import {
  useNavigate,
  useParams,
} from "react-router-dom";

import { useAuth } from "../../../../contexts/AuthContext";
import { getAccessToken } from "../../../../awsAuth";
import ProfileListing from "../../ProfileListing";

import "./BusinessListingsAll.scss";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "https://cvanto-api.onrender.com";

export default function BusinessListingsAll() {
  const navigate = useNavigate();
  const params = useParams();
  const { userId } = useAuth();

  const viewedUserId =
    params.userId || userId;

  const [listings, setListings] =
    useState([]);

  const [businessName, setBusinessName] =
    useState("Оголошення");

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [query, setQuery] =
    useState("");

  const [sort, setSort] =
    useState("newest");

  useEffect(() => {
    let alive = true;

    async function loadListings() {
      if (!viewedUserId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const token = await getAccessToken()
          .catch(() => null);

        const headers = token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {};

        const listingsRes = await fetch(
          `${API_BASE}/api/listings/user/${encodeURIComponent(
            viewedUserId
          )}`,
          {
            headers,
          }
        );

        const listingsData =
          await listingsRes
            .json()
            .catch(() => []);

        if (!listingsRes.ok) {
          throw new Error(
            listingsData?.error ||
              "Не вдалося завантажити оголошення"
          );
        }

        const profileRes = await fetch(
          `${API_BASE}/api/business/profile/${encodeURIComponent(
            viewedUserId
          )}`,
          {
            headers,
          }
        );

        const profileData =
          await profileRes
            .json()
            .catch(() => ({}));

        if (!alive) return;

        const allListings = Array.isArray(
          listingsData
        )
          ? listingsData
          : Array.isArray(listingsData?.items)
          ? listingsData.items
          : [];

        const normalListings =
          allListings.filter((item) => {
            const category = String(
              item?.category || ""
            ).toLowerCase();

            const moduleKey = String(
              item?.businessModule ||
                item?.moduleKey ||
                item?.entityType ||
                ""
            ).toLowerCase();

            const listingType = String(
              item?.listingType ||
                item?.type ||
                ""
            ).toLowerCase();

            return (
              category !== "карʼєра" &&
              category !== "кар'єра" &&
              moduleKey !== "vacancies" &&
              moduleKey !==
                "business_vacancy" &&
              listingType !== "wanted"
            );
          });

        setListings(normalListings);

        setBusinessName(
          profileData?.userBase?.username ||
            profileData?.profile?.title ||
            profileData?.profile?.hero
              ?.title ||
            "Оголошення"
        );
      } catch (err) {
        console.error(err);

        if (alive) {
          setError(
            err.message ||
              "Помилка завантаження"
          );
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    }

    loadListings();

    return () => {
      alive = false;
    };
  }, [viewedUserId]);

  const filteredListings =
    useMemo(() => {
      let next = Array.isArray(listings)
        ? [...listings]
        : [];

      const normalizedQuery = query
        .trim()
        .toLowerCase();

      if (normalizedQuery) {
        next = next.filter((item) => {
          const searchableText = [
            item?.title,
            item?.description,
            item?.category,
            item?.location,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          return searchableText.includes(
            normalizedQuery
          );
        });
      }

      if (sort === "oldest") {
        next.sort(
          (a, b) =>
            Number(a?.createdAt || 0) -
            Number(b?.createdAt || 0)
        );
      }

      if (sort === "newest") {
        next.sort(
          (a, b) =>
            Number(b?.createdAt || 0) -
            Number(a?.createdAt || 0)
        );
      }

      if (sort === "priceAsc") {
        next.sort(
          (a, b) =>
            Number(a?.price || 0) -
            Number(b?.price || 0)
        );
      }

      if (sort === "priceDesc") {
        next.sort(
          (a, b) =>
            Number(b?.price || 0) -
            Number(a?.price || 0)
        );
      }

      return next;
    }, [listings, query, sort]);

  if (loading) {
    return (
      <main className="blAll">
        <div className="blAll__state">
          Завантаження оголошень...
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="blAll">
        <div className="blAll__state blAll__state--error">
          {error}
        </div>
      </main>
    );
  }

  return (
    <main className="blAll">
      <header className="blAll__hero">
        <button
          type="button"
          className="blAll__back"
          onClick={() => navigate(-1)}
          aria-label="Назад"
        >
          <ArrowLeft
            size={22}
            strokeWidth={2.5}
          />
        </button>

        <div>
          <span>Бізнес-профіль</span>

          <h1>{businessName}</h1>

          <p>
            {listings.length} оголошень
          </p>
        </div>
      </header>

      <section className="blAll__filters">
        <label className="blAll__search">
          <Search
            size={18}
            strokeWidth={2.5}
          />

          <input
            value={query}
            onChange={(event) =>
              setQuery(event.target.value)
            }
            placeholder="Пошук оголошень"
          />
        </label>

        <label className="blAll__sort">
          <SlidersHorizontal
            size={16}
            strokeWidth={2.5}
          />

          <select
            value={sort}
            onChange={(event) =>
              setSort(event.target.value)
            }
          >
            <option value="newest">
              Нові спочатку
            </option>

            <option value="oldest">
              Старі спочатку
            </option>

            <option value="priceAsc">
              Дешевші спочатку
            </option>

            <option value="priceDesc">
              Дорожчі спочатку
            </option>
          </select>
        </label>
      </section>

      {filteredListings.length === 0 ? (
        <section className="blAll__empty">
          <strong>
            Оголошень не знайдено
          </strong>

          <p>
            Спробуй змінити пошуковий
            запит.
          </p>
        </section>
      ) : (
        <section className="blAll__content">
          <ProfileListing
            listings={filteredListings}
            userId={viewedUserId}
            onOpenListing={(listingId) =>
              navigate(
                `/listing/${listingId}`
              )
            }
          />
        </section>
      )}
    </main>
  );
}